import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Plus, Search } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  formFieldInputClass,
  formFieldLabelClass,
  formFieldMenuClass,
} from '@/components/shared/formFieldClasses'

type AddOptionResult = { ok: true } | { ok: false; error: string }

type Props = {
  value: string
  onChange: (value: string) => void
  options: string[]
  label?: string
  placeholder?: string
  hint?: string
  error?: string
  disabled?: boolean
  className?: string
  inputClassName?: string
  onPersistOption?: (value: string) => AddOptionResult
  findDuplicate?: (value: string, options: string[]) => string | null
}

function normalizeQuery(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

export function SearchableCreatableField({
  value,
  onChange,
  options,
  label,
  placeholder = 'Buscar ou digitar…',
  hint,
  error,
  disabled = false,
  className,
  inputClassName,
  onPersistOption,
  findDuplicate,
}: Props) {
  const [query, setQuery] = useState(value)
  const [open, setOpen] = useState(false)
  const [localError, setLocalError] = useState<string | undefined>()
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setQuery(value)
  }, [value])

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const mergedOptions = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    for (const item of options) {
      const trimmed = item.trim()
      if (!trimmed) continue
      const key = trimmed.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      list.push(trimmed)
    }
    if (value.trim()) {
      const key = value.trim().toLowerCase()
      if (!seen.has(key)) list.unshift(value.trim())
    }
    return list.sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [options, value])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return mergedOptions.slice(0, 40)
    return mergedOptions.filter((item) => item.toLowerCase().includes(q)).slice(0, 40)
  }, [mergedOptions, query])

  const normalizedQuery = normalizeQuery(query)
  const exactMatch = mergedOptions.some(
    (item) => item.toLowerCase() === normalizedQuery.toLowerCase(),
  )
  const displayError = error ?? localError

  const commitValue = useCallback(
    (next: string) => {
      const trimmed = normalizeQuery(next)
      if (!trimmed) {
        onChange('')
        setQuery('')
        setLocalError(undefined)
        return
      }
      if (findDuplicate) {
        const existing = findDuplicate(trimmed, mergedOptions)
        if (existing) {
          // Opção já listada — seleciona com grafia canônica da lista
          onChange(existing)
          setQuery(existing)
          setLocalError(undefined)
          return
        }
      }
      onChange(trimmed)
      setQuery(trimmed)
      setLocalError(undefined)
    },
    [findDuplicate, mergedOptions, onChange],
  )

  const handlePersist = () => {
    if (!normalizedQuery || !onPersistOption) return
    const result = onPersistOption(normalizedQuery)
    if (!result.ok) {
      setLocalError(result.error)
      return
    }
    commitValue(normalizedQuery)
    setOpen(false)
  }

  return (
    <div ref={wrapperRef} className={cn('relative flex flex-col gap-1.5', className)}>
      {label ? (
        <label
          className={formFieldLabelClass}
          style={{ fontSize: 'var(--font-size-label)' }}
        >
          {label}
        </label>
      ) : null}

      <div className='relative'>
        <Search
          size={14}
          className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]'
        />
        <input
          type='text'
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setLocalError(undefined)
            setOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (!normalizedQuery) return
              if (!exactMatch && onPersistOption) {
                handlePersist()
              } else {
                commitValue(normalizedQuery)
                setOpen(false)
              }
            }
            if (e.key === 'Escape') setOpen(false)
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (normalizedQuery && normalizedQuery !== value) {
                commitValue(normalizedQuery)
              }
              setOpen(false)
            }, 150)
          }}
          className={cn(
            formFieldInputClass,
            'min-h-[38px] py-2 pl-9 pr-9 text-[13px]',
            displayError && 'border-[#e5484d] focus:border-[#e5484d] focus:ring-[rgba(229,72,77,0.15)]',
            inputClassName,
          )}
        />
        <button
          type='button'
          tabIndex={-1}
          onClick={() => setOpen((prev) => !prev)}
          className='absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]'
        >
          <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {hint && !displayError ? (
        <p className='text-[11px] text-[var(--text-muted)]'>{hint}</p>
      ) : null}
      {displayError ? <p className='text-[11px] text-red-400'>{displayError}</p> : null}

      {open && !disabled ? (
        <div
          className={cn(
            formFieldMenuClass,
            'absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] shadow-xl',
          )}
        >
          {filtered.length === 0 && !normalizedQuery ? (
            <p className='px-3 py-2 text-xs text-[var(--text-muted)]'>Digite para buscar ou criar</p>
          ) : (
            filtered.map((item) => (
              <button
                key={item}
                type='button'
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  commitValue(item)
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full px-3 py-2 text-left text-[13px] transition-colors hover:bg-[var(--glass-bg)]',
                  item.toLowerCase() === value.toLowerCase()
                    ? 'text-accent'
                    : 'text-[var(--text-primary)]',
                )}
              >
                {item}
              </button>
            ))
          )}

          {normalizedQuery && !exactMatch && onPersistOption ? (
            <button
              type='button'
              onMouseDown={(e) => e.preventDefault()}
              onClick={handlePersist}
              className='flex w-full items-center gap-2 border-t border-[var(--glass-border)] px-3 py-2.5 text-left text-[12px] font-semibold text-accent hover:bg-accent/10'
            >
              <Plus size={13} />
              Adicionar &quot;{normalizedQuery}&quot;
            </button>
          ) : null}

          {normalizedQuery && !exactMatch && !onPersistOption ? (
            <button
              type='button'
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                commitValue(normalizedQuery)
                setOpen(false)
              }}
              className='flex w-full border-t border-[var(--glass-border)] px-3 py-2 text-left text-[12px] text-[var(--text-muted)] hover:bg-[var(--glass-bg)]'
            >
              Usar &quot;{normalizedQuery}&quot;
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
